// Axon "Sign in with Axon": Authorization Code + PKCE, driven by axchat-identity.
// Identity mints the PKCE verifier and state, keeps them in an HttpOnly signed
// cookie, and redirects to the Axon IdP as its confidential client. The IdP
// returns to this SPA's /axon/oauth/callback, which hands the code and state
// back to identity (tokenPath) for the exchange. The resulting access token is
// kept in memory and sent as a Bearer to the chat API; session.ts renews it
// before it expires, and restores it from the refresh cookie after a reload.

import { config } from '../config';
import { endSession, setSubject, setToken } from './session';

/** Sends the browser to axchat-identity, which redirects on to the Axon IdP. */
export async function startAxonLogin(): Promise<void> {
  // Identity sends the IdP its own AXON_REDIRECT_URI unless told otherwise.
  // `vite dev` is served from localhost, which identity's allowlist names, and
  // the code has to come back to the page that started the login.
  const query = import.meta.env.DEV
    ? `?redirect_uri=${encodeURIComponent(`${window.location.origin}/axon/oauth/callback`)}`
    : '';
  window.location.href = `${config.loginPath}${query}`;
}

/**
 * Handles the IdP redirect: hands the code and state to axchat-identity, which
 * checks the state against its cookie, exchanges the code, and returns the
 * access token, kept in memory.
 */
export async function completeAxonLogin(): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state');
  const idpError = params.get('error');

  if (idpError) {
    throw new Error(idpError);
  }
  if (!state) {
    throw new Error('invalid OAuth state');
  }
  if (!code) {
    throw new Error('missing authorization code');
  }

  const resp = await fetch(config.tokenPath, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, state }),
  });

  if (!resp.ok) {
    throw new Error(`token exchange failed: HTTP ${resp.status}`);
  }

  // The refresh token is not in the body: axchat-identity sets it as an
  // HttpOnly cookie on this response, for `refreshToken()` to spend later.
  const json = (await resp.json()) as {
    access_token?: unknown;
    expires_in?: unknown;
    claims?: { sub?: unknown };
  };
  // Checked, not cast: a body without a token must fail here, not look like a
  // sign-in that then 401s.
  if (typeof json.access_token !== 'string' || !json.access_token) {
    throw new Error('token exchange returned no access token');
  }
  const expiresIn =
    typeof json.expires_in === 'number' && Number.isFinite(json.expires_in)
      ? json.expires_in
      : undefined;
  setToken(json.access_token, expiresIn);
  setSubject(typeof json.claims?.sub === 'string' ? json.claims.sub : undefined);
}

/**
 * The Sign out button: ends the session browser-wide (see `endSession`), then
 * goes back to the IdP.
 */
export async function signOut(): Promise<void> {
  await endSession();
  await startAxonLogin();
}
